
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import FilterWidget from '../shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import {COL, sqlSum, sqlSumByDocType, getTableName} from '@/worker/db/sqlHelpers';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
    components: {
        AmountDisplayerFilterable,
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        SlPhModuleOnlyToggle,
    },
})
export default class AgentSalesNumberDisplayer extends FilterWidget {
    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
    > = [];

    public salesCount = 0;
    public salesAmount = '' || 0;
    public activeCustomersCount = 0;
    public invoiceCount = 0;

    public get permissionIds(): PermissionsGroup[] {
        return ['sales', 'customers'];
    }

    public get dateFormatted() {
        return this.selectedDateRange;
    }

    public get currentYear() {
        return moment(this.selectedAsOfDate).year();
    }

    public get currency() {
        return gdbx.currencySymbol;
    }

    public get decimal() {
        return gdbx.numDecimal;
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public get expensiveHook() {
        const {
            selectedDateRange,
            selectedAsOfDate,
            selectedStockItems,
            selectedAgents,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        } = this;
        return JSON.stringify([
            selectedDateRange,
            selectedAsOfDate,
            selectedStockItems,
            selectedAgents,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        ]);
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;

        const queryHead = await this._ensureTablesLoaded(
            ['sales', 'creditNotes', 'customerTrades'] as any[],
        );
        this.checkInterrupted(jobId);

        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const arTable = getTableName('customerTrades');

        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
            ? ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
            : '';

        let salesAmount = 0;

        if (this.slPhModuleOnly) {
            const [slRows, slCNRows] = await Promise.all([
                queryHead.rawQuery(`
                    SELECT
                        ${sqlSumByDocType(['IV', 'CS'], COL.AMOUNT, 'iv_cs_amount')},
                        ${sqlSumByDocType(['DN'], COL.AMOUNT, 'dn_amount')}
                    FROM ${salesTable}
                    WHERE ${dateFilter}${agentFilter}
                `),
                queryHead.rawQuery(`
                    SELECT ${sqlSum(COL.AMOUNT, 'cn_amount')}
                    FROM ${cnTable}
                    WHERE ${dateFilter}${agentFilter}
                `),
            ]);

            const slIvCs = slRows[0] ? Number(slRows[0].iv_cs_amount) || 0 : 0;
            const slDN = slRows[0] ? Number(slRows[0].dn_amount) || 0 : 0;
            const slCN = slCNRows[0] ? Number(slCNRows[0].cn_amount) || 0 : 0;

            salesAmount = slIvCs
                - (this.includeCreditNote ? slCN : 0)
                + (this.includeDebitNote ? slDN : 0);
        } else {
            const [arRows, arRefsResult] = await Promise.all([
                queryHead.rawQuery(`
                    SELECT
                        ${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
                        ${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
                        ${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')},
                        COUNT(DISTINCT ${COL.CODE}) as customerCount
                    FROM ${arTable}
                    WHERE ${dateFilter}${agentFilter}
                `),
                // AR DocRefs query (expand pipe-delimited DOCREFS for case counting)
                queryHead.rawQuery(`
                    SELECT ${COL.DOCTYPE} as docType, COUNT(DISTINCT ref) as refCount
                    FROM (
                        SELECT ${COL.DOCTYPE},
                            UNNEST(string_split("DOCREFS", '|')) as ref
                        FROM ${arTable}
                        WHERE ${dateFilter}${agentFilter}
                    ) sub
                    WHERE ref IS NOT NULL AND TRIM(ref) != ''
                    GROUP BY ${COL.DOCTYPE}
                `),
            ]);

            const arIv = arRows[0] ? Number(arRows[0].iv_amount) || 0 : 0;
            const arCN = arRows[0] ? Number(arRows[0].cn_amount) || 0 : 0;
            const arDN = arRows[0] ? Number(arRows[0].dn_amount) || 0 : 0;

            salesAmount = arIv
                - (this.includeCreditNote ? arCN : 0)
                + (this.includeDebitNote ? arDN : 0);

            // AR invoice count from expanded DOCREFS
            const getRefCount = (docType: string): number => {
                const row = arRefsResult.find((r: any) => String(r.docType) === docType);
                return row ? (Number(row.refCount) || 0) : 0;
            };
            this.invoiceCount = getRefCount('IV')
                + (this.includeDebitNote ? getRefCount('DN') : 0)
                - (this.includeCreditNote ? getRefCount('CN') : 0);
            this.activeCustomersCount = arRows[0]
                ? Number(arRows[0].customerCount) || 0 : 0;
        }

        this.checkInterrupted(jobId);

        if (this.slPhModuleOnly) {
            // SL invoice count (distinct DOCNO from sales)
            const ivDocTypeFilter = ` AND ${COL.DOCTYPE} IN ('IV')`;
            const [invoiceRows, customerRows] = await Promise.all([
                queryHead.rawQuery(`
                    SELECT COUNT(DISTINCT ${COL.DOCNO}) as invoiceCount
                    FROM ${salesTable}
                    WHERE ${dateFilter}${agentFilter}${ivDocTypeFilter}
                `),
                queryHead.rawQuery(`
                    SELECT COUNT(DISTINCT ${COL.CODE}) as customerCount
                    FROM ${salesTable}
                    WHERE ${dateFilter}${agentFilter} AND ${COL.DOCTYPE} IN ('IV', 'CS')
                `),
            ]);
            this.checkInterrupted(jobId);

            this.invoiceCount = invoiceRows[0] ? Number(invoiceRows[0].invoiceCount) || 0 : 0;
            this.activeCustomersCount = customerRows[0] ? Number(customerRows[0].customerCount) || 0 : 0;
        }
        this.salesAmount = salesAmount;

        this.saveHistory('salesAmount', 'invoiceCount', 'activeCustomersCount');
    }
}
